export type DecisionTraceSource = {
  id: string;
  label: string;
  detail?: string;
  confidence?: number;
  url?: string;
};

export type DecisionTraceCheck = {
  label: string;
  status: 'passed' | 'warning' | 'blocked' | 'info';
  detail?: string;
};

export type DecisionTraceLatency = {
  label: string;
  ms: number;
};

export type DecisionTraceModel = {
  route?: string;
  routeSource?: string;
  sources: DecisionTraceSource[];
  assumptions: string[];
  checks: DecisionTraceCheck[];
  blockers: string[];
  confidenceBand?: 'high' | 'medium' | 'low' | 'unknown';
  confidenceScore?: number;
  latencies: DecisionTraceLatency[];
};

export type RunDetailsToolCall = {
  id: string;
  label: string;
  status?: string;
};

export type RunDetailsModel = {
  runId?: string;
  threadId?: string;
  route?: string;
  artifactId?: string;
  artifactTitle?: string;
  toolCalls: RunDetailsToolCall[];
  retrievalCount?: number;
  citationCount?: number;
  approvalDecisions: string[];
  persistenceStatus?: string;
};

const UNSAFE_FIELD_RE = /(chain.?of.?thought|reasoning|thought|scratchpad|deliberation|private_analysis|analysis_trace)/i;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function safeString(value: unknown): string | undefined {
  const text = normalizeString(value);
  if (!text || UNSAFE_FIELD_RE.test(text)) return undefined;
  return text;
}

function normalizeArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function normalizeSafeStringArray(value: unknown): string[] {
  return normalizeArray(value)
    .map((entry) => {
      if (typeof entry === 'string') return safeString(entry);
      const record = asRecord(entry);
      return safeString(record?.label)
        || safeString(record?.summary)
        || safeString(record?.description)
        || safeString(record?.title);
    })
    .filter((entry): entry is string => Boolean(entry))
    .slice(0, 6);
}

function humanize(value: unknown): string | undefined {
  const raw = safeString(value);
  if (!raw) return undefined;
  const normalized = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function confidenceBand(score: number | undefined): DecisionTraceModel['confidenceBand'] {
  if (typeof score !== 'number') return undefined;
  if (score >= 0.85) return 'high';
  if (score >= 0.65) return 'medium';
  return 'low';
}

function average(values: number[]): number | undefined {
  const valid = values.filter((value) => Number.isFinite(value));
  if (!valid.length) return undefined;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function getMetaFromMessage(message: unknown): Record<string, unknown> | null {
  const record = asRecord(message);
  return asRecord(record?.meta);
}

function getProvenance(meta: Record<string, unknown> | null): Record<string, unknown> | null {
  return asRecord(meta?.provenance) || meta;
}

function getRuntimeState(meta: Record<string, unknown> | null): Record<string, unknown> | null {
  return asRecord(asRecord(meta?.runtimeTool)?.state);
}

function routeFrom(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): {
  route?: string;
  routeSource?: string;
} {
  const provenance = getProvenance(meta);
  const routing = asRecord(provenance?.routing);
  const supervisor = asRecord(provenance?.supervisor);
  const runtimeState = getRuntimeState(meta);
  const runtimePayload = asRecord(runtimeState?.resultPayload);
  const plannerRoute = normalizeArray(supervisor?.plannerRoute)
    .map((entry) => humanize(entry))
    .filter((entry): entry is string => Boolean(entry));

  const route =
    humanize(runtimePayload?.route)
    || humanize(routing?.intentKind)
    || (plannerRoute.length ? plannerRoute.join(' -> ') : undefined)
    || humanize(runtimeState?.toolId)
    || humanize(artifact?.agentType);

  return {
    ...(route ? { route } : {}),
    ...(humanize(routing?.routeDecisionSource) ? { routeSource: humanize(routing?.routeDecisionSource) } : {}),
  };
}

function collectSources(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): DecisionTraceSource[] {
  const provenance = getProvenance(meta);
  const evidenceTrace = asRecord(meta?.evidenceDecisionTrace) || asRecord(provenance?.evidenceDecisionTrace);
  const citations = [
    ...normalizeArray(provenance?.citationsV2),
    ...normalizeArray(meta?.citationsV2),
    ...normalizeArray(asRecord(artifact?.format)?.citations),
  ];
  const memories = [
    ...normalizeArray(meta?.memories),
    ...normalizeArray(provenance?.memoriesUsed),
  ];
  const ragSources = [
    ...normalizeArray(asRecord(artifact?.rag)?.sources),
    ...normalizeArray(asRecord(artifact?.researcher)?.sources),
  ];
  const evidenceSources = normalizeArray(evidenceTrace?.sourcesUsed);

  const sources: DecisionTraceSource[] = [];
  const seen = new Set<string>();

  const add = (source: DecisionTraceSource) => {
    const key = source.url || source.id || source.label;
    if (!key || seen.has(key)) return;
    seen.add(key);
    sources.push(source);
  };

  citations.forEach((entry, index) => {
    const record = asRecord(entry);
    if (!record) return;
    const label = safeString(record.title)
      || safeString(record.docId)
      || safeString(record.url)
      || `Citation ${index + 1}`;
    const detail = safeString(record.claim) || safeString(record.snippet) || safeString(record.evidenceSnippet);
    add({
      id: safeString(record.docId) || safeString(record.url) || `citation-${index}`,
      label,
      ...(detail ? { detail } : {}),
      ...(numberValue(record.confidence) ? { confidence: numberValue(record.confidence) } : {}),
      ...(safeString(record.url) ? { url: safeString(record.url) } : {}),
    });
  });

  memories.forEach((entry, index) => {
    const record = typeof entry === 'string' ? { id: entry } : asRecord(entry);
    if (!record) return;
    const label = safeString(record.title)
      || safeString(record.text)
      || safeString(record.url)
      || safeString(record.id)
      || `Memory ${index + 1}`;
    add({
      id: safeString(record.id) || `memory-${index}`,
      label,
      ...(safeString(record.path) ? { detail: safeString(record.path) } : {}),
      ...(safeString(record.url) ? { url: safeString(record.url) } : {}),
    });
  });

  ragSources.forEach((entry, index) => {
    const record = asRecord(entry);
    if (!record) return;
    const label = safeString(record.title)
      || safeString(record.url)
      || safeString(record.docId)
      || safeString(record.id)
      || `Source ${index + 1}`;
    add({
      id: safeString(record.id) || safeString(record.docId) || safeString(record.url) || `source-${index}`,
      label,
      ...(safeString(record.text) || safeString(record.snippet) ? { detail: safeString(record.text) || safeString(record.snippet) } : {}),
      ...(numberValue(record.score) ? { confidence: numberValue(record.score) } : {}),
      ...(safeString(record.url) ? { url: safeString(record.url) } : {}),
    });
  });

  evidenceSources.forEach((entry, index) => {
    const text = safeString(entry);
    if (!text) return;
    const parts = text.split(':');
    add({
      id: parts[0] || `evidence-${index}`,
      label: parts.slice(1).join(':') || text,
    });
  });

  return sources.slice(0, 8);
}

function collectChecks(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): DecisionTraceCheck[] {
  const provenance = getProvenance(meta);
  const evidenceTrace = asRecord(meta?.evidenceDecisionTrace) || asRecord(provenance?.evidenceDecisionTrace);
  const rag = asRecord(provenance?.rag);
  const sufficiency = asRecord(rag?.sufficiency);
  const retrievalQuality = asRecord(rag?.retrievalQuality);
  const consistency = asRecord(provenance?.consistency) || asRecord(artifact?.consistency);
  const autoFix = asRecord(provenance?.autoFix) || asRecord(artifact?.autoFix);
  const pillars = asRecord(provenance?.pillars) || asRecord(artifact?.pillars);
  const runtimeState = getRuntimeState(meta);
  const policyIssues = [
    ...normalizeArray(provenance?.policyIssues),
    ...normalizeArray(meta?.policyIssues),
    ...normalizeArray(artifact?.policyIssues),
    ...normalizeArray(asRecord(asRecord(artifact?.format)?.policy)?.issues),
  ];

  const checks: DecisionTraceCheck[] = [];
  if (sufficiency && typeof sufficiency.sufficient === 'boolean') {
    checks.push({
      label: 'Context sufficiency',
      status: sufficiency.sufficient ? 'passed' : 'warning',
      ...(safeString(sufficiency.reason) ? { detail: safeString(sufficiency.reason) } : {}),
    });
  }

  if (evidenceTrace && typeof evidenceTrace.externalSearchNeeded === 'boolean') {
    const used = evidenceTrace.externalSearchUsed === true;
    const providerStatus = asRecord(evidenceTrace.providerStatus);
    const usedJina = asRecord(providerStatus?.jina_official)?.used === true;
    const usedTavily = asRecord(providerStatus?.tavily)?.used === true;
    const label = usedTavily
      ? 'Used Tavily web search'
      : usedJina
        ? 'Used official source extraction'
        : used
          ? 'Used external evidence'
          : 'Skipped web search';
    checks.push({
      label,
      status: used || evidenceTrace.externalSearchNeeded === false ? 'info' : 'warning',
      ...(safeString(evidenceTrace.searchWideningReason) || safeString(evidenceTrace.skippedReason) || safeString(evidenceTrace.reason)
        ? { detail: safeString(evidenceTrace.searchWideningReason) || safeString(evidenceTrace.skippedReason) || safeString(evidenceTrace.reason) }
        : {}),
    });
  }

  if (retrievalQuality) {
    const mode = humanize(retrievalQuality.retrievalMode) || 'Retrieval';
    checks.push({
      label: `${mode} retrieval`,
      status: 'info',
      detail: [
        numberValue(retrievalQuality.denseCandidates) ? `${retrievalQuality.denseCandidates} dense` : '',
        numberValue(retrievalQuality.lexicalCandidates) ? `${retrievalQuality.lexicalCandidates} lexical` : '',
        numberValue(retrievalQuality.fusedCandidates) ? `${retrievalQuality.fusedCandidates} fused` : '',
      ].filter(Boolean).join(' / ') || undefined,
    });
  }

  if (policyIssues.length) {
    const blocked = policyIssues.some((issue) => {
      const record = asRecord(issue);
      const severity = normalizeString(record?.severity);
      return severity === 'block' || severity === 'high' || severity === 'critical' || normalizeString(record?.category) === 'citation';
    });
    checks.push({
      label: 'Policy review',
      status: blocked ? 'blocked' : 'warning',
      detail: `${policyIssues.length} ${policyIssues.length === 1 ? 'issue' : 'issues'}`,
    });
  }

  if (consistency && typeof consistency.contradictions === 'number') {
    checks.push({
      label: 'Consistency check',
      status: consistency.contradictions > 0 ? 'warning' : 'passed',
      detail: `${consistency.contradictions} contradictions`,
    });
  }

  if (autoFix && (typeof autoFix.appliedCount === 'number' || typeof autoFix.totalSuggestions === 'number')) {
    checks.push({
      label: 'Revision pass',
      status: 'info',
      detail: `${autoFix.appliedCount || 0} of ${autoFix.totalSuggestions || 0} suggestions applied`,
    });
  }

  if (pillars && typeof pillars.score === 'number') {
    checks.push({
      label: 'Message pillars',
      status: pillars.score >= (numberValue(pillars.threshold) || 0.7) ? 'passed' : 'warning',
      detail: `Score ${pillars.score.toFixed(2)}`,
    });
  }

  if (runtimeState) {
    const status = humanize(runtimeState.status);
    checks.push({
      label: 'Runtime tool',
      status: normalizeString(runtimeState.error) ? 'blocked' : status === 'Completed' ? 'passed' : 'info',
      ...(status ? { detail: status } : {}),
    });
  }

  return checks.slice(0, 8);
}

function collectBlockers(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): string[] {
  const provenance = getProvenance(meta);
  const evidenceTrace = asRecord(meta?.evidenceDecisionTrace) || asRecord(provenance?.evidenceDecisionTrace);
  const policyIssues = [
    ...normalizeArray(provenance?.policyIssues),
    ...normalizeArray(meta?.policyIssues),
    ...normalizeArray(artifact?.policyIssues),
  ];
  const blockers = policyIssues
    .map((issue) => asRecord(issue))
    .filter((issue): issue is Record<string, unknown> => {
      if (!issue) return false;
      const severity = normalizeString(issue.severity);
      return severity === 'block' || severity === 'high' || severity === 'critical' || normalizeString(issue.category) === 'citation';
    })
    .map((issue) => safeString(issue.message) || safeString(issue.code))
    .filter((entry): entry is string => Boolean(entry));

  const runtimeState = getRuntimeState(meta);
  const error = safeString(runtimeState?.error) || safeString(asRecord(meta?.runtimeTool)?.error);
  if (error) blockers.push(error);
  blockers.push(...normalizeSafeStringArray(evidenceTrace?.blockers));

  return blockers.slice(0, 5);
}

function collectConfidence(meta: Record<string, unknown> | null, sources: DecisionTraceSource[], artifact?: Record<string, unknown> | null): {
  confidenceScore?: number;
  confidenceBand?: DecisionTraceModel['confidenceBand'];
} {
  const provenance = getProvenance(meta);
  const routing = asRecord(provenance?.routing);
  const pillars = asRecord(provenance?.pillars) || asRecord(artifact?.pillars);
  const scores = [
    numberValue(routing?.llmConfidence),
    numberValue(provenance?.confidence),
    numberValue(asRecord(provenance?.effectivePreferences)?.confidence),
    numberValue(pillars?.score),
    ...sources.map((source) => source.confidence).filter((value): value is number => typeof value === 'number'),
  ].filter((value): value is number => typeof value === 'number');
  const confidenceScore = average(scores);
  const band = confidenceBand(confidenceScore);
  return {
    ...(typeof confidenceScore === 'number' ? { confidenceScore } : {}),
    ...(band ? { confidenceBand: band } : {}),
  };
}

function collectLatencies(meta: Record<string, unknown> | null): DecisionTraceLatency[] {
  const provenance = getProvenance(meta);
  const timings = asRecord(provenance?.timings);
  const latencies: DecisionTraceLatency[] = [];
  if (timings) {
    Object.entries(timings).forEach(([key, value]) => {
      const ms = numberValue(value);
      const label = humanize(key);
      if (typeof ms === 'number' && label) latencies.push({ label, ms });
    });
  }
  const rerankerLatency = numberValue(asRecord(asRecord(asRecord(provenance?.rag)?.retrievalQuality)?.reranker)?.latencyMs);
  if (typeof rerankerLatency === 'number') {
    latencies.push({ label: 'Reranker', ms: rerankerLatency });
  }
  const runtimeState = getRuntimeState(meta);
  const runtimeLatency = numberValue(runtimeState?.latencyMs) || numberValue(runtimeState?.durationMs);
  if (typeof runtimeLatency === 'number') {
    latencies.push({ label: 'Runtime tool', ms: runtimeLatency });
  }
  return latencies.slice(0, 6);
}

export function buildDecisionTraceFromMeta(metaValue: unknown): DecisionTraceModel | null {
  const meta = asRecord(metaValue);
  if (!meta) return null;
  return buildDecisionTrace(meta);
}

function buildDecisionTrace(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): DecisionTraceModel | null {
  const route = routeFrom(meta, artifact);
  const sources = collectSources(meta, artifact);
  const checks = collectChecks(meta, artifact);
  const blockers = collectBlockers(meta, artifact);
  const provenance = getProvenance(meta);
  const runtimeState = getRuntimeState(meta);
  const assumptions = [
    ...normalizeSafeStringArray(provenance?.assumptions),
    ...normalizeSafeStringArray(asRecord(meta?.evidenceDecisionTrace)?.assumptions),
    ...normalizeSafeStringArray(runtimeState?.assumptions),
    ...normalizeSafeStringArray(artifact?.assumptions),
  ].slice(0, 6);
  const confidence = collectConfidence(meta, sources, artifact);
  const latencies = collectLatencies(meta);

  if (
    !route.route
    && !route.routeSource
    && !sources.length
    && !assumptions.length
    && !checks.length
    && !blockers.length
    && !confidence.confidenceBand
    && !latencies.length
  ) {
    return null;
  }

  return {
    ...route,
    sources,
    assumptions,
    checks,
    blockers,
    ...confidence,
    latencies,
  };
}

export function buildDecisionTraceFromMessage(message: unknown): DecisionTraceModel | null {
  return buildDecisionTrace(getMetaFromMessage(message));
}

export function buildDecisionTraceFromArtifact(artifactValue: unknown): DecisionTraceModel | null {
  const artifact = asRecord(artifactValue);
  if (!artifact) return null;
  const syntheticMeta: Record<string, unknown> = {
    ...(artifact.supervisorMeta ? { provenance: { supervisor: artifact.supervisorMeta } } : {}),
    ...(artifact.runId ? { runId: artifact.runId } : {}),
    ...(artifact.sourceConversationId ? { threadId: artifact.sourceConversationId } : {}),
    ...(artifact.policyIssues ? { policyIssues: artifact.policyIssues } : {}),
  };
  return buildDecisionTrace(syntheticMeta, artifact);
}

function collectToolCalls(meta: Record<string, unknown> | null): RunDetailsToolCall[] {
  const runtimeState = getRuntimeState(meta);
  const rawCalls = [
    ...normalizeArray(meta?.toolCalls),
    ...normalizeArray(meta?.tool_calls),
    ...normalizeArray(asRecord(meta?.provenance)?.toolCalls),
    ...normalizeArray(asRecord(meta?.provenance)?.tool_calls),
  ];
  const calls: RunDetailsToolCall[] = rawCalls
    .map((entry, index) => {
      const record = asRecord(entry);
      if (!record) return null;
      const label = humanize(record.name) || humanize(record.toolName) || humanize(record.toolId) || `Tool ${index + 1}`;
      return {
        id: safeString(record.id) || safeString(record.toolCallId) || `tool-${index}`,
        label,
        ...(humanize(record.status) ? { status: humanize(record.status) } : {}),
      };
    })
    .filter((entry): entry is RunDetailsToolCall => !!entry);

  if (runtimeState) {
    calls.push({
      id: safeString(runtimeState.requestId) || safeString(runtimeState.runId) || 'runtime-tool',
      label: humanize(runtimeState.label) || humanize(runtimeState.toolId) || 'Runtime tool',
      ...(humanize(runtimeState.status) ? { status: humanize(runtimeState.status) } : {}),
    });
  }

  const seen = new Set<string>();
  return calls.filter((call) => {
    const key = `${call.id}:${call.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 8);
}

function retrievalCount(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): number | undefined {
  const provenance = getProvenance(meta);
  const metrics = asRecord(asRecord(provenance?.rag)?.sufficiency)?.metrics;
  const uniqueDocs = numberValue(asRecord(metrics)?.uniqueDocs);
  if (typeof uniqueDocs === 'number') return uniqueDocs;
  const sources = normalizeArray(asRecord(artifact?.rag)?.sources);
  return sources.length || undefined;
}

function citationCount(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): number | undefined {
  const provenance = getProvenance(meta);
  const count = [
    ...normalizeArray(provenance?.citationsV2),
    ...normalizeArray(meta?.citationsV2),
    ...normalizeArray(asRecord(artifact?.format)?.citations),
  ].length;
  return count || undefined;
}

function approvalDecisions(meta: Record<string, unknown> | null, artifact?: Record<string, unknown> | null): string[] {
  const decisions: string[] = [];
  const feedback = asRecord(meta?.artifactFeedback);
  const action = humanize(feedback?.action);
  if (action) decisions.push(action);
  const approvalStatus = humanize(artifact?.approvalStatus);
  if (approvalStatus) decisions.push(approvalStatus);
  return Array.from(new Set(decisions));
}

export function buildRunDetailsFromMessage(messageValue: unknown): RunDetailsModel | null {
  const message = asRecord(messageValue);
  const meta = getMetaFromMessage(messageValue);
  if (!meta && !message) return null;
  const runtimeState = getRuntimeState(meta);
  const route = routeFrom(meta);
  const runId = safeString(meta?.runId)
    || safeString(asRecord(meta?.provenance)?.runId)
    || safeString(runtimeState?.runId)
    || safeString(runtimeState?.requestId);
  const threadId = safeString(meta?.threadId)
    || safeString(asRecord(meta?.provenance)?.threadId)
    || safeString(runtimeState?.threadId)
    || safeString(runtimeState?.sourceConversationId);
  const details: RunDetailsModel = {
    ...(runId ? { runId } : {}),
    ...(threadId ? { threadId } : {}),
    ...(route.route ? { route: route.route } : {}),
    toolCalls: collectToolCalls(meta),
    ...(retrievalCount(meta) ? { retrievalCount: retrievalCount(meta) } : {}),
    ...(citationCount(meta) ? { citationCount: citationCount(meta) } : {}),
    approvalDecisions: approvalDecisions(meta),
    ...(humanize(runtimeState?.status) ? { persistenceStatus: humanize(runtimeState?.status) } : {}),
  };
  return details.runId || details.threadId || details.route || details.toolCalls.length || details.retrievalCount || details.citationCount || details.approvalDecisions.length
    ? details
    : null;
}

export function buildRunDetailsFromArtifact(artifactValue: unknown): RunDetailsModel | null {
  const artifact = asRecord(artifactValue);
  if (!artifact) return null;
  const meta: Record<string, unknown> = {
    ...(artifact.runId ? { runId: artifact.runId } : {}),
    ...(artifact.sourceConversationId ? { threadId: artifact.sourceConversationId } : {}),
    ...(artifact.supervisorMeta ? { provenance: { supervisor: artifact.supervisorMeta } } : {}),
  };
  const route = routeFrom(meta, artifact);
  const details: RunDetailsModel = {
    ...(safeString(artifact.runId) || safeString(asRecord(artifact.supervisorMeta)?.runId)
      ? { runId: safeString(artifact.runId) || safeString(asRecord(artifact.supervisorMeta)?.runId) }
      : {}),
    ...(safeString(artifact.sourceConversationId) ? { threadId: safeString(artifact.sourceConversationId) } : {}),
    ...(route.route ? { route: route.route } : {}),
    ...(safeString(artifact.id) ? { artifactId: safeString(artifact.id) } : {}),
    ...(safeString(artifact.title) ? { artifactTitle: safeString(artifact.title) } : {}),
    toolCalls: [],
    ...(retrievalCount(meta, artifact) ? { retrievalCount: retrievalCount(meta, artifact) } : {}),
    ...(citationCount(meta, artifact) ? { citationCount: citationCount(meta, artifact) } : {}),
    approvalDecisions: approvalDecisions(meta, artifact),
    persistenceStatus: artifact.isSavedToLibrary ? 'Saved to library' : 'Draft only',
  };
  return details.runId || details.threadId || details.route || details.artifactId || details.retrievalCount || details.citationCount || details.approvalDecisions.length
    ? details
    : null;
}
